import React, { useEffect, useState } from 'react';
import { Box, CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import PvdConfigurationsTab from './pvd/PvdConfigurationsTab';
import EmergencyConfigurationsTab from './emergency/EmergencyConfigurationsTab';
import ConfigurationsRouteBar from './ConfigurationsRouteBar';
import { useBannerHeight } from '@/context_providers/banner_context/BannerHeightContext';

type TabKey = 'pvd' | 'emergency';

const DRAWER_WIDTH = 240;

const isValidTab = (s: string | null): s is TabKey =>
  s === 'pvd' || s === 'emergency';

const CompassConfigurationsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { defaultPageBoxPadding } = useBannerHeight();

  const queryTab = new URLSearchParams(location.search).get('tab');
  const initialTab: TabKey = isValidTab(queryTab) ? queryTab : 'pvd';

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  // Sync if the URL query param changes externally (e.g. browser back/forward)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTab = params.get('tab');
    if (isValidTab(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <CssBaseline />
      <ConfigurationsRouteBar activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Content area — offset from the permanent drawer on desktop */}
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          zIndex: 1,
          ...(isMobile
            ? { px: 2, pt: 8 }
            : {
                pl: `${DRAWER_WIDTH + 8}px`,
                pr: '8px',
                height: (theme) =>
                  `calc(100dvh - ${theme.spacing(defaultPageBoxPadding)})`,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }),
        }}
      >
        {activeTab === 'pvd' && <PvdConfigurationsTab />}
        {activeTab === 'emergency' && <EmergencyConfigurationsTab />}
      </Box>
    </>
  );
};

export default CompassConfigurationsPage;
